/* @jsx h */
import { defineComponent, h } from "vue";
import { Dropdown, useLocalization } from "@lemmonhq/uikit";
import type { CoinEarnRule } from "@lemmonhq/types";
import { EarnRuleFields } from "./EarnRuleFields";

/**
 * The variants a merchant can add today. `referral` is deliberately absent: it
 * is in the type union but needs fraud controls before it can be offered —
 * docs/plans/loyalty/referral_earn_rule.md.
 */
const ADDABLE_RULE_TYPES = ["spend", "product", "category", "visit"] as const;

type AddableRuleType = (typeof ADDABLE_RULE_TYPES)[number];

const RULE_ICONS: Record<CoinEarnRule["type"], string> = {
  spend: "payments",
  product: "lunch_dining",
  category: "category",
  visit: "storefront",
  referral: "group_add",
};

const defaultRule = (type: AddableRuleType): CoinEarnRule => {
  switch (type) {
    case "spend":
      return { type: "spend", coins_per_unit: 1, unit_amount: 10 };
    case "product":
      return { type: "product", product_ids: [], coins: 1 };
    case "category":
      return { type: "category", category_ids: [], coins: 1 };
    case "visit":
      return { type: "visit", coins: 1, max_per_day: 1 };
  }
};

export type EarnRulesEditorProps = {
  value: CoinEarnRule[];
  coinLabel: string;
  onChange: (rules: CoinEarnRule[]) => void;
  allowedRuleTypes?: AddableRuleType[];
};

export const EarnRulesEditor = defineComponent<EarnRulesEditorProps>({
  name: "EarnRulesEditor",
  props: ["value", "coinLabel", "onChange", "allowedRuleTypes"],
  setup(props) {
    const { t } = useLocalization();

    const addRule = (type: AddableRuleType) =>
      props.onChange([...props.value, defaultRule(type)]);

    const updateRule = (index: number, rule: CoinEarnRule) =>
      props.onChange(props.value.map((r, i) => (i === index ? rule : r)));

    const removeRule = (index: number) =>
      props.onChange(props.value.filter((_, i) => i !== index));

    const renderAddMenu = () => (
      <Dropdown
        placement="bottom-end"
        offset={8}
        closeOnContentClick
        contentClass="menu menu-sm w-56 rounded-xl border border-base-content/10 bg-base-300 p-1.5 shadow-xl"
      >
        {{
          trigger: () => (
            <button
              type="button"
              class="btn btn-sm btn-ghost gap-1.5 text-primary"
            >
              <span class="material-symbols-outlined text-base">add</span>
              {t("promotions.coins.rule_add")}
            </button>
          ),
          default: () =>
            ADDABLE_RULE_TYPES.filter(
              (type) =>
                !props.allowedRuleTypes ||
                props.allowedRuleTypes.includes(type),
            ).map((type) => (
              <li key={type}>
                <a onClick={() => addRule(type)}>
                  <span class="material-symbols-outlined text-base">
                    {RULE_ICONS[type]}
                  </span>
                  {t(`promotions.coins.rule_type_${type}`)}
                </a>
              </li>
            )),
        }}
      </Dropdown>
    );

    return () => (
      <div class="space-y-3">
        <div class="flex items-center justify-between gap-3">
          <p class="text-sm text-base-content/50">
            {t("promotions.coins.rules_description")}
          </p>
          {renderAddMenu()}
        </div>

        {props.value.length === 0 ? (
          <div class="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-base-300 py-8 text-base-content/40">
            <span class="material-symbols-outlined text-3xl">toll</span>
            <p class="text-sm">{t("promotions.coins.rules_empty")}</p>
          </div>
        ) : (
          props.value.map((rule, index) => (
            <div
              key={`${rule.type}-${index}`}
              class="rounded-xl border border-base-300 bg-base-100 p-4"
            >
              <div class="flex items-center justify-between gap-3 mb-2">
                <h4 class="flex items-center gap-2 font-medium text-accent">
                  <span class="material-symbols-outlined text-base text-primary">
                    {RULE_ICONS[rule.type]}
                  </span>
                  {t(`promotions.coins.rule_type_${rule.type}`)}
                </h4>
                <button
                  type="button"
                  onClick={() => removeRule(index)}
                  title={t("promotions.coins.rule_remove")}
                  aria-label={t("promotions.coins.rule_remove")}
                  class="btn btn-xs btn-ghost text-error hover:bg-error/10"
                >
                  <span class="material-symbols-outlined text-base">
                    delete
                  </span>
                </button>
              </div>

              <EarnRuleFields
                value={rule}
                coinLabel={props.coinLabel}
                onChange={(updated: CoinEarnRule) => updateRule(index, updated)}
              />
            </div>
          ))
        )}
      </div>
    );
  },
});
